import React, { useCallback, useMemo } from 'react';
import { AvailableIcons, Icon, Skeleton } from '../../../../../../../components-dummy';
import {
  defaultEmailVariables,
  emailVariableLabels,
  EmailVariablesKeys,
  emailVariableTooltips,
} from './types';
import { CampaignDraft } from '../../../../../types/campaign-draft';
import { WidgetMaker } from '../../../../../types/widget-maker';
import {
  LabelStyled,
  Section,
  TextBoxStyled,
  TextStyled,
  TooltipStyled,
} from '../../CampaignSideBar.styles';
import { useGetPrimaryCatalogLocales } from 'src/components-bl/VisualEditor/hooks/useGetPrimaryCatalogLocales';
import { Dispatch } from 'src/components-dummy/types';
import { useDispatch } from 'react-redux';
import { LocaleVariableSelect } from './LocaleVariableSelect';
interface EmailVariablesSectionProps {
  widgetMaker: Partial<WidgetMaker>;
  onChange: (campaign: CampaignDraft) => void;
  shopId: number;
}

export const EmailVariablesSection = ({
  widgetMaker,
  onChange,
  shopId,
}: EmailVariablesSectionProps) => {
  const dispatch = useDispatch<Dispatch>();

  const { locales, defaultLocale, loading } = useGetPrimaryCatalogLocales({
    shopId,
    appDispatch: dispatch,
  });
  const { variables } = widgetMaker;
  const emailVariables = useMemo(
    () => (variables && Object.keys(variables).length > 0 ? variables : defaultEmailVariables),
    [variables]
  );

  const onVariablesChange = useCallback(
    (key, value) => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          variables: { ...emailVariables, [key]: value },
        },
      });
    },
    [onChange, emailVariables]
  );

  const onLocaleChange = useCallback(
    value => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          locale: value,
        },
      });
    },
    [onChange, widgetMaker]
  );

  return (
    <Section>
      <TextStyled>
        Email Variables power personalized emails by setting user-specific attributes i.e SKU in
        abandoned cart, User ID etc.
      </TextStyled>
      {loading ? (
        <Skeleton height={40} width={322} />
      ) : (
        locales?.length > 0 && (
          <LocaleVariableSelect
            locale={widgetMaker.locale ?? defaultLocale}
            defaultLocale={defaultLocale}
            locales={locales}
            onChange={onLocaleChange}
          />
        )
      )}

      {Object.entries(emailVariables).map(([key, value]) => {
        return (
          <>
            <LabelStyled>
              {emailVariableLabels[key as EmailVariablesKeys] || key} Variable
              <TooltipStyled
                position='bottom center'
                value={emailVariableTooltips[key as EmailVariablesKeys] || key}
              >
                <Icon name={AvailableIcons.TooltipQuestionmarkGray} />
              </TooltipStyled>
            </LabelStyled>
            <TextBoxStyled
              key={key}
              placeholder='{{productId.key}}'
              value={value}
              onChange={newValue => onVariablesChange(key, newValue)}
            />
          </>
        );
      })}
    </Section>
  );
};
